import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((data) => {
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setModules(data.modules || []);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applySession(data);
    } catch {
      setUser(null);
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback establishes the session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applySession(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applySession(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setModules([]);
    }
  };

  const canAccess = (id) => modules.some((m) => m.id === id);

  return (
    <AuthContext.Provider
      value={{ user, setUser, modules, loading, login, register, loginWithGoogle, logout, applySession, canAccess, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
